/**
 * `next/dynamic` — React.lazy plus the two options this codebase passes.
 *
 * The shell uses it 48 times, once per federated route, always in the same shape:
 *
 *     dynamic(() => import('tmsa-app/TMSADashboard'), { ssr: false, loading: () => <AppLoader /> })
 *
 * `ssr: false` is now meaningless — nothing renders on a server — but it is accepted and
 * ignored so the ported call sites need no edit, and so a remote that ships its own
 * `dynamic()` call keeps working.
 */

import React, { Suspense, lazy } from 'react';

type Loader<P> = () => Promise<{ default: React.ComponentType<P> } | React.ComponentType<P>>;

interface DynamicOptions {
  /** Accepted and ignored — there is no server render to opt out of. */
  ssr?: boolean;
  loading?: React.ComponentType<{ error?: Error | null; isLoading?: boolean }>;
}

export default function dynamic<P extends object>(
  loader: Loader<P>,
  options: DynamicOptions = {},
): React.ComponentType<P> {
  const LazyComponent = lazy(async () => {
    const mod = await loader();
    // A remote container can expose either shape. `.default` where it is an ES module,
    // the namespace itself where the exposed file is a bare component.
    return 'default' in mod ? (mod as { default: React.ComponentType<P> }) : { default: mod };
  });

  const Loading = options.loading;

  function DynamicComponent(props: P) {
    return (
      <Suspense fallback={Loading ? <Loading isLoading /> : null}>
        <LazyComponent {...(props as P & React.JSX.IntrinsicAttributes)} />
      </Suspense>
    );
  }

  DynamicComponent.displayName = 'DynamicComponent';
  return DynamicComponent;
}
